/**
 * Federal + NY State + NYC tax liability helpers — Phase 7 Session 1.
 *
 * **Authoritative-grade math for FIRE planning, not for filing.** Every
 * helper carries an inline disclaimer: actual tax liability has too
 * many sources of variance (credits, AMT preferences, NIIT, additional
 * Medicare, state-specific subtractions, prior-year carryovers) for a
 * pure-function projector to be 100% accurate. Hearth's job is to give
 * the household a directionally-correct quarterly + year-end estimate
 * with marginal/effective rates surfaced, so they can make tax-aware
 * spending and Roth-conversion decisions. Filing math belongs in
 * TurboTax / a CPA workflow.
 *
 * **Sources** (all retrieved 2026-05-08):
 *
 * - **IRC § 1** — Federal ordinary income brackets.
 * - **IRC § 1(h)** — LTCG / qualified dividends preferential rates.
 *   LTCG stacks ABOVE ordinary income — the rate applied to the LTCG
 *   slice depends on where TOTAL taxable income falls, with ordinary
 *   filling the lower brackets first.
 * - **IRC § 6654** — Estimated tax safe-harbor rules. Pay at least:
 *     (a) 90% of current-year liability, OR
 *     (b) 100% of prior-year tax (110% if prior AGI > $150K MFJ /
 *         $75K MFS).
 *   Hearth uses (b) as the primary signal because it's deterministic
 *   given prior-year-paid; (a) requires the projection itself.
 * - **NY Tax Law § 601** — NY State ordinary income brackets.
 * - **NYC Admin Code § 11-1701** — NYC resident income tax brackets.
 *
 * **What this DOES NOT compute** (deferred or out of scope):
 *
 * - **AMT** — separate helper at `amt.ts` (Cleave 8); kicks in mostly
 *   for ISO exercises, large itemized deductions, large LTCG.
 * - **NIIT** (Net Investment Income Tax, 3.8%, IRC § 1411) — phase-out
 *   at $200K single / $250K MFJ MAGI. Phase 11 may add.
 * - **Additional Medicare Tax** (0.9% wages, IRC § 3101(b)(2)) — phase
 *   11.
 * - **FICA / Self-Employment Tax** — Phase 11 once the income_events
 *   table differentiates W-2 vs 1099 reliably.
 * - **State-specific subtractions** beyond NY 529 — Phase 11 (multi-
 *   state hardening).
 * - **Itemized deductions** (Schedule A) — v1 assumes standard
 *   deduction always. Phase 11 polish layers in itemized once the
 *   housing + spending + charity data converges (mortgage interest,
 *   property tax, charitable, SALT cap).
 * - **Credits** (CTC, EITC, AOTC, LLC, Saver's, Foreign Tax) — Phase
 *   11.
 *
 * **Web mirrors this** at `web/src/lib/util/tax-liability.ts` per the
 * 5.1 lock (web does NOT import @hearth/shared).
 */

import type {
  FilingStatus,
  TaxBracket,
  FederalBracketTable,
  StateBracketTable,
} from "../data/tax-brackets-2025.js";
import { FEDERAL_2025, NY_2025, NYC_2025 } from "../data/tax-brackets-2025.js";
import { FEDERAL_2026, NY_2026, NYC_2026 } from "../data/tax-brackets-2026.js";

// Re-export type for convenient downstream import.
export type { FilingStatus };

/** Bracket-table dispatcher. Throws on unsupported year — Hearth ships
 *  2025 + 2026 only; ramping to additional years is Phase 11 polish. */
export function federalBracketsFor(tax_year: number): FederalBracketTable {
  if (tax_year === 2025) return FEDERAL_2025;
  if (tax_year === 2026) return FEDERAL_2026;
  throw new Error(
    `tax-liability: unsupported tax_year ${tax_year} (Hearth ships 2025 + 2026)`,
  );
}

export function nyBracketsFor(tax_year: number): StateBracketTable {
  if (tax_year === 2025) return NY_2025;
  if (tax_year === 2026) return NY_2026;
  throw new Error(
    `tax-liability: unsupported tax_year ${tax_year} for NY (Hearth ships 2025 + 2026)`,
  );
}

export function nycBracketsFor(tax_year: number): StateBracketTable {
  if (tax_year === 2025) return NYC_2025;
  if (tax_year === 2026) return NYC_2026;
  throw new Error(
    `tax-liability: unsupported tax_year ${tax_year} for NYC (Hearth ships 2025 + 2026)`,
  );
}

// === bracket math ==========================================================

/**
 * Apply a bracket schedule to a positive taxable-income amount in
 * cents. Returns total tax owed in cents. Pure; no side effects.
 *
 * Bracket convention: half-open ranges [min, max). Top bracket has
 * max=null. Brackets MUST be sorted ascending and contiguous.
 */
export function applyBrackets(
  taxable_cents: number,
  brackets: readonly TaxBracket[],
): number {
  if (!Number.isInteger(taxable_cents)) {
    throw new Error(
      `applyBrackets: taxable_cents must be integer, got ${taxable_cents}`,
    );
  }
  if (taxable_cents <= 0) return 0;
  let tax = 0;
  for (const b of brackets) {
    if (taxable_cents <= b.min_cents) break;
    const top = b.max_cents === null ? taxable_cents : Math.min(taxable_cents, b.max_cents);
    const slice = top - b.min_cents;
    if (slice > 0) tax += (slice * b.rate_pct) / 100;
  }
  return Math.round(tax);
}

/**
 * Marginal rate at a given taxable-income level. Returns the rate of
 * the bracket the dollar would land in.
 */
export function marginalRateAt(
  taxable_cents: number,
  brackets: readonly TaxBracket[],
): number {
  if (taxable_cents < 0) return 0;
  for (const b of brackets) {
    if (b.max_cents === null) return b.rate_pct;
    if (taxable_cents < b.max_cents) return b.rate_pct;
  }
  return brackets[brackets.length - 1]?.rate_pct ?? 0;
}

// === federal liability =====================================================

export interface FederalTaxLiabilityInput {
  /** Wages, business income, interest, non-qualified dividends, short-
   *  term capital gains, Roth conversions, etc. — anything taxed at
   *  ordinary rates BEFORE the standard deduction is applied. */
  readonly ordinary_income_cents: number;
  /** Long-term capital gains. Taxed at IRC § 1(h) preferential rates. */
  readonly ltcg_cents: number;
  /** Qualified dividends. Same § 1(h) preferential rates as LTCG. */
  readonly qdivs_cents: number;
  /** Standard or itemized deduction reduction to AGI before bracket
   *  application. Defaults to the year's standard deduction when null. */
  readonly itemized_deduction_cents?: number | null;
  readonly filing_status: FilingStatus;
  readonly tax_year: number;
}

export interface FederalTaxLiabilityResult {
  /** Total federal tax liability, integer cents. */
  readonly liability_cents: number;
  /** Tax on the ordinary-income slice. */
  readonly ordinary_tax_cents: number;
  /** Tax on the LTCG + qualified-dividends slice. */
  readonly ltcg_tax_cents: number;
  /** Marginal federal rate at the highest dollar of taxable income. */
  readonly marginal_rate_pct: number;
  /** Effective rate = liability / total_income (ordinary + LTCG +
   *  QDIV before deductions). */
  readonly effective_rate_pct: number;
  /** Taxable income after the standard/itemized deduction. */
  readonly taxable_income_cents: number;
}

/**
 * Federal tax liability with proper LTCG/QDIV stacking.
 *
 * Math (per IRC § 1(h) qualified-dividends-and-capital-gain-tax
 * worksheet, simplified):
 *
 *   1. taxable_income = ordinary + LTCG + QDIV − deduction
 *   2. ordinary_taxable = max(0, taxable_income − LTCG − QDIV)
 *   3. ordinary_tax = applyBrackets(ordinary_taxable, ordinary_brackets)
 *   4. ltcg_taxable = LTCG + QDIV (clamped to remaining_taxable_income)
 *   5. ltcg_tax = LTCG taxed at the rate corresponding to where
 *      `ordinary_taxable + dollar` lands in the LTCG schedule —
 *      i.e., LTCG fills the LTCG brackets ABOVE ordinary_taxable.
 */
export function federalTaxLiability(
  input: FederalTaxLiabilityInput,
): FederalTaxLiabilityResult {
  const { ordinary_income_cents, ltcg_cents, qdivs_cents, filing_status, tax_year } = input;
  const fed = federalBracketsFor(tax_year);
  const deduction =
    input.itemized_deduction_cents ?? fed.standard_deduction_cents[filing_status];
  const total_income = ordinary_income_cents + ltcg_cents + qdivs_cents;
  const taxable_income = Math.max(0, total_income - deduction);

  // The deduction reduces ordinary first (matches IRS Schedule D Tax
  // Worksheet line 14 convention).
  const preferential = ltcg_cents + qdivs_cents;
  const ordinary_taxable = Math.max(0, taxable_income - preferential);
  const preferential_taxable = Math.max(0, taxable_income - ordinary_taxable);

  // Ordinary tax.
  const ordinary_brackets = fed.ordinary[filing_status];
  const ordinary_tax = applyBrackets(ordinary_taxable, ordinary_brackets);

  // LTCG/QDIV stacks ABOVE ordinary_taxable in the LTCG schedule.
  const ltcg_brackets = fed.ltcg[filing_status];
  const ltcg_tax = applyStackedLtcg(
    ordinary_taxable,
    preferential_taxable,
    ltcg_brackets,
  );

  const liability = ordinary_tax + ltcg_tax;
  const marginal_rate_pct = marginalRateAt(taxable_income, ordinary_brackets);
  const effective_rate_pct =
    total_income > 0 ? (liability / total_income) * 100 : 0;

  return {
    liability_cents: liability,
    ordinary_tax_cents: ordinary_tax,
    ltcg_tax_cents: ltcg_tax,
    marginal_rate_pct,
    effective_rate_pct: Math.round(effective_rate_pct * 100) / 100,
    taxable_income_cents: taxable_income,
  };
}

/**
 * Tax LTCG starting at `floor_cents` (the top of ordinary income) up
 * to `floor_cents + amount_cents`. Each LTCG bracket contributes
 * `slice × rate / 100` to the result.
 */
function applyStackedLtcg(
  floor_cents: number,
  amount_cents: number,
  brackets: readonly TaxBracket[],
): number {
  if (amount_cents <= 0) return 0;
  const top = floor_cents + amount_cents;
  let tax = 0;
  for (const b of brackets) {
    const bracket_top = b.max_cents === null ? top : b.max_cents;
    const slice_min = Math.max(b.min_cents, floor_cents);
    const slice_max = Math.min(bracket_top, top);
    if (slice_max > slice_min) {
      tax += ((slice_max - slice_min) * b.rate_pct) / 100;
    }
    if (b.max_cents !== null && bracket_top >= top) break;
  }
  return Math.round(tax);
}

// === NY State liability ====================================================

export interface StateTaxLiabilityInput {
  /** NY/NYC taxable income BEFORE the state standard deduction. NY
   *  taxes both ordinary AND capital gains at ordinary rates — there's
   *  no preferential rate on long-term gains at the state level. */
  readonly taxable_income_cents: number;
  readonly filing_status: FilingStatus;
  readonly tax_year: number;
  /** Optional override for the standard deduction (e.g., if itemized
   *  exceeds standard at the state level). Defaults to standard. */
  readonly state_deduction_cents?: number | null;
}

export interface StateTaxLiabilityResult {
  readonly liability_cents: number;
  readonly marginal_rate_pct: number;
  readonly effective_rate_pct: number;
  readonly taxable_after_deduction_cents: number;
}

export function nyTaxLiability(
  input: StateTaxLiabilityInput,
): StateTaxLiabilityResult {
  const { taxable_income_cents, filing_status, tax_year } = input;
  const ny = nyBracketsFor(tax_year);
  const deduction = input.state_deduction_cents ?? ny.standard_deduction_cents[filing_status];
  const taxable_after = Math.max(0, taxable_income_cents - deduction);
  const brackets = ny.ordinary[filing_status];
  const liability = applyBrackets(taxable_after, brackets);
  const marginal = marginalRateAt(taxable_after, brackets);
  const effective =
    taxable_income_cents > 0 ? (liability / taxable_income_cents) * 100 : 0;
  return {
    liability_cents: liability,
    marginal_rate_pct: marginal,
    effective_rate_pct: Math.round(effective * 100) / 100,
    taxable_after_deduction_cents: taxable_after,
  };
}

/**
 * NYC resident income tax. Non-residents pay $0 NYC; caller decides
 * whether to invoke this helper.
 *
 * NYC does NOT have its own standard deduction — the brackets apply
 * directly to NY State taxable income. Caller should pass
 * `taxable_income_cents` AFTER the NY standard deduction (same value
 * used in `nyTaxLiability` post-deduction).
 */
export function nycTaxLiability(
  input: StateTaxLiabilityInput,
): StateTaxLiabilityResult {
  const { taxable_income_cents, filing_status, tax_year } = input;
  const nyc = nycBracketsFor(tax_year);
  // NYC has no separate standard deduction — but caller may pass the
  // already-NY-deducted income; treat input as taxable directly.
  const deduction = input.state_deduction_cents ?? 0;
  const taxable_after = Math.max(0, taxable_income_cents - deduction);
  const brackets = nyc.ordinary[filing_status];
  const liability = applyBrackets(taxable_after, brackets);
  const marginal = marginalRateAt(taxable_after, brackets);
  const effective =
    taxable_income_cents > 0 ? (liability / taxable_income_cents) * 100 : 0;
  return {
    liability_cents: liability,
    marginal_rate_pct: marginal,
    effective_rate_pct: Math.round(effective * 100) / 100,
    taxable_after_deduction_cents: taxable_after,
  };
}

// === aggregate liability ===================================================

export interface TaxLiabilityInput {
  readonly ordinary_income_cents: number;
  readonly ltcg_cents: number;
  readonly qdivs_cents: number;
  readonly filing_status: FilingStatus;
  readonly tax_year: number;
  /** Whether to compute NY State liability. Default true (Hearth's
   *  user is NY-resident; multi-state hardening is Phase 15). */
  readonly include_ny?: boolean;
  /** Whether to compute NYC resident liability. Default true. */
  readonly include_nyc?: boolean;
  /** Optional federal itemized deduction override. */
  readonly federal_itemized_deduction_cents?: number | null;
}

export interface TaxLiabilityResult {
  readonly federal_cents: number;
  readonly ny_cents: number;
  readonly nyc_cents: number;
  readonly total_cents: number;
  readonly federal: FederalTaxLiabilityResult;
  readonly ny: StateTaxLiabilityResult | null;
  readonly nyc: StateTaxLiabilityResult | null;
  /** Composite marginal rate = federal + NY + NYC marginal rates at
   *  highest dollar (sums apparent rates; doesn't account for state-
   *  tax-deductibility on federal Schedule A — Phase 11 polish if/when
   *  Hearth supports itemized). */
  readonly composite_marginal_rate_pct: number;
  /** Composite effective rate = total / gross income. */
  readonly composite_effective_rate_pct: number;
}

export function taxLiability(input: TaxLiabilityInput): TaxLiabilityResult {
  const include_ny = input.include_ny ?? true;
  const include_nyc = input.include_nyc ?? true;

  const federal = federalTaxLiability({
    ordinary_income_cents: input.ordinary_income_cents,
    ltcg_cents: input.ltcg_cents,
    qdivs_cents: input.qdivs_cents,
    filing_status: input.filing_status,
    tax_year: input.tax_year,
    itemized_deduction_cents: input.federal_itemized_deduction_cents ?? null,
  });

  // NY taxes capital gains at ordinary rates, so feed full income.
  const ny_input: StateTaxLiabilityInput = {
    taxable_income_cents:
      input.ordinary_income_cents + input.ltcg_cents + input.qdivs_cents,
    filing_status: input.filing_status,
    tax_year: input.tax_year,
  };
  const ny = include_ny ? nyTaxLiability(ny_input) : null;

  // NYC piggybacks on NY taxable income (post-NY-deduction).
  const nyc_input: StateTaxLiabilityInput = {
    taxable_income_cents: ny ? ny.taxable_after_deduction_cents : ny_input.taxable_income_cents,
    filing_status: input.filing_status,
    tax_year: input.tax_year,
  };
  const nyc = include_nyc ? nycTaxLiability(nyc_input) : null;

  const total =
    federal.liability_cents + (ny?.liability_cents ?? 0) + (nyc?.liability_cents ?? 0);
  const total_income = input.ordinary_income_cents + input.ltcg_cents + input.qdivs_cents;
  const composite_marginal =
    federal.marginal_rate_pct + (ny?.marginal_rate_pct ?? 0) + (nyc?.marginal_rate_pct ?? 0);
  const composite_effective =
    total_income > 0 ? (total / total_income) * 100 : 0;

  return {
    federal_cents: federal.liability_cents,
    ny_cents: ny?.liability_cents ?? 0,
    nyc_cents: nyc?.liability_cents ?? 0,
    total_cents: total,
    federal,
    ny,
    nyc,
    composite_marginal_rate_pct: Math.round(composite_marginal * 100) / 100,
    composite_effective_rate_pct: Math.round(composite_effective * 100) / 100,
  };
}

// === safe-harbor ===========================================================

export type SafeHarborStatus =
  /** Not yet meeting any safe harbor — likely to owe penalty. */
  | "not_safe"
  /** Meeting 90% current-year safe harbor (IRC § 6654(d)(1)(B)(i)). */
  | "90_pct"
  /** Meeting 100% prior-year safe harbor (low-AGI filers, IRC §
   *  6654(d)(1)(B)(ii)). */
  | "100_pct"
  /** Meeting 110% prior-year safe harbor (high-AGI filers, AGI > $150K
   *  MFJ / $75K MFS, IRC § 6654(d)(1)(C)). */
  | "110_pct";

export interface SafeHarborInput {
  readonly prior_year_agi_cents: number;
  readonly prior_year_tax_cents: number;
  readonly current_year_paid_cents: number;
  readonly projected_current_year_liability_cents: number;
  readonly filing_status: FilingStatus;
}

export interface SafeHarborResult {
  readonly status: SafeHarborStatus;
  /** Required cumulative withholding+estimated to reach the safest
   *  threshold (the higher of 90% current or 100/110% prior). */
  readonly required_payment_cents: number;
  /** Shortfall vs `required_payment_cents`; 0 if safe. */
  readonly shortfall_cents: number;
  /** Which threshold was applied for the prior-year side (100 or 110). */
  readonly prior_year_threshold_pct: 100 | 110;
}

/**
 * Determine which safe-harbor threshold the household has met.
 *
 * Rule (IRC § 6654):
 * - High-AGI filers (prior AGI > $150K MFJ / $75K MFS): 110% of prior
 *   year, OR 90% of current year.
 * - Other filers: 100% of prior year, OR 90% of current year.
 * - "Prior-year" requires a full 12-month return (not modeled — caller
 *   responsibility).
 */
export function safeHarborStatus(input: SafeHarborInput): SafeHarborResult {
  const high_agi_threshold_cents =
    input.filing_status === "mfs" ? 75_000_00 : 150_000_00;
  const prior_year_threshold_pct: 100 | 110 =
    input.prior_year_agi_cents > high_agi_threshold_cents ? 110 : 100;
  const prior_year_required =
    Math.round((input.prior_year_tax_cents * prior_year_threshold_pct) / 100);
  const current_year_required = Math.round(
    input.projected_current_year_liability_cents * 0.9,
  );
  // Caller is safe if they meet the LOWER of the two thresholds (it's
  // the "EITHER (a) OR (b)" rule).
  const required_payment_cents = Math.min(
    prior_year_required,
    current_year_required,
  );

  let status: SafeHarborStatus;
  if (input.current_year_paid_cents >= prior_year_required) {
    status = prior_year_threshold_pct === 110 ? "110_pct" : "100_pct";
  } else if (input.current_year_paid_cents >= current_year_required) {
    status = "90_pct";
  } else {
    status = "not_safe";
  }

  return {
    status,
    required_payment_cents,
    shortfall_cents: Math.max(
      0,
      required_payment_cents - input.current_year_paid_cents,
    ),
    prior_year_threshold_pct,
  };
}
